// src/HomePage.jsx
import React, { useEffect, useState } from "react";
import ParallaxCard from "./components/ParallaxCard";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import Header from "./components/Header";
import AnimatedButton from "./components/AnimatedButton";
import AnimatedNavLink from "./components/AnimatedNavLink";
import themeColors from "./themeColors";
import useParallax from "./useParallax";
import Footer from "./components/Footer";
import { slugOrId } from "./utils/slugOrId";
import { Heart, MessageCircle, Eye } from "lucide-react";
import FloatingCtaCard from "./components/FloatingCtaCard";
import MobileNewsletterStrip from "./components/MobileNewsletterStrip";
import { API_BASE, mediaUrl } from "./utils/apiBase";
import { useLocation } from "react-router-dom"; 
import BrandName from "./components/BrandName";


export default function HomePage() {
  useParallax();

  const location = useLocation();   

  /* ───────────────────────────────────────── state ── */
  const [latestNews, setLatestNews] = useState(null);
  const [latestGame, setLatestGame] = useState(null);

  // carousel
  const [slideImages, setSlideImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  // contact form
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [formStatus, setFormStatus] = useState(null);

  const navigate = useNavigate();

  /* ─────────────────────────────────────── carousel ── */
  useEffect(() => {
    if (!slideImages.length) return;
    const iv = setInterval(
      () => setCurrentSlide(i => (i + 1) % slideImages.length),
      5000
    );
    return () => clearInterval(iv);
  }, [slideImages]);

  

  /* ───────────────────────────── fetch newest game/news ── */
  useEffect(() => {
    (async () => {
      try {
        /* 1️⃣ newest **non-legacy** game first */
        let res = await axios.get(
            `${API_BASE}/games` +
            "?filters[Legacy][$eq]=false" +
            "&populate[Sections][populate]=Media" +
            "&populate=TeaserImage" +
            "&sort=createdAt:desc" +
            "&pagination[pageSize]=1"
        );

        if (!res.data.data.length) {
          /* 2️⃣ fallback: any game */
          res = await axios.get(
              `${API_BASE}/games` +
              "?populate[Sections][populate]=Media" +
              "&populate=TeaserImage" +
              "&sort=createdAt:desc" +
              "&pagination[pageSize]=1"
          );
        }

        if (res.data.data.length) {
          const game = res.data.data[0];
          setLatestGame(game);

          const imgs =
            game.Sections?.flatMap(s => s.Media || [])
              .filter(Boolean)
              .map(m => m.url || m.attributes?.url) || [];
          setSlideImages(imgs);
        }

        /* newest news post */
        const newsRes = await axios.get(`${API_BASE}/news-items`, {
          params: {
            sort: "createdAt:desc",
            "pagination[pageSize]": 1,
            "fields[0]": "Title",
            "fields[1]": "slug",
            "fields[2]": "views",
            "populate[Sections][populate]": "Media",
            "populate[Tags]": true,
            },
            });
        if (newsRes.data.data.length) setLatestNews(newsRes.data.data[0]);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [location.pathname]); // ← reruns when route is “/” again

  /* preferred banner image for parallax */
  const teaserUrl =
    latestGame?.TeaserImage?.url || latestGame?.TeaserImage?.attributes?.url || null;

  /* ───────────────────────────────────────── render ── */
  return (
    <>
      <Header />

      <main className="min-h-screen text-white overflow-x-hidden"
          style={{ backgroundColor: themeColors.darkBlue }}>
        {/* ───────── Hero ───────── */}
        <section id="home" className="scroll-mt-[-16px] relative w-full h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full z-0">
            <video autoPlay muted loop playsInline className="w-full h-full object-cover">
              <source src="/videos/windswept-hero.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="absolute inset-0 z-10 tf-hero-scrim" />
          <div className="absolute inset-0 z-10 tf-noise" />
          <div className="relative z-20 text-center px-6 text-white pt-24 md:pt-32">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4 tracking-wide">
              TWINFRAME STUDIOS
            </h1>
            <p className="text-base sm:text-lg md:text-xl mb-6">
             <strong>STYLIZED WORLDS</strong> FOR <strong>SHARED ADVENTURES</strong>
            </p>
            {/* TODO: Steam wishlist URL — re-enable when page exists
            <AnimatedButton text="Wishlist Windswept" href="#" />
            */}
            <AnimatedButton
              text="Follow us on Discord"
              href="https://discord.gg/TdPsv5xujp"
            />
          </div>
           {/* 👇 sentinel at the *very* end of the hero section */}
            <div id="cta-sentinel-hero" className="h-px" />
        </section>

        {/* ───────── About ───────── */}
        <section id="story" className="scroll-mt-[-16px] relative overflow-hidden pt-[60px] pb-20 px-8 md:px-24 text-white">
          <div className="absolute inset-0" style={{ background: themeColors.storyBg }} />
          <div className="absolute inset-0 tf-noise" />
          <div className="absolute inset-0 tf-vignette" />
          <div className="relative z-10">
          <h2 className="tf-section-title text-3xl sm:text-4xl font-bold mb-8">OUR STORY</h2>
          <p className="max-w-3xl text-lg leading-relaxed text-white/90">
          <b>Have you ever been excited by a game... only to wish you could play that adventure with someone?</b><br></br><br></br>
          
          I believe the most memorable games are remembered because of <i>who you experience them with.</i><br></br><br></br>

          That's why I founded <BrandName /> <br></br><br></br>
          To create stylized couch co-op games filled with exploration, discovery, and action. 
          The kind of journeys that I think should be enjoyed together with someone sitting right beside you.<br></br><br></br>

          <b>Stylized Worlds for Shared Adventures</b><br></br><br></br>

          Before <BrandName />, I spent years creating games and co-leading the indie studio Gamistry. Best known to most mobile gamers for Munch Time. <i>(IOS Game of the Week 2012)</i><br></br>
          Today, I'm putting that experience into building the kind of games I've always wanted to play.<br></br><br></br>

          Right now, we’re building our very first original adventure. <br></br>
          More on that soon.

          <br></br><br></br><i>— Alex Kentie, Founder & Game Developer</i>
          </p>
          </div>
        </section>

        {/* ───────── Games ───────── */}
        <section id="games" className="scroll-mt-[-16px] pt-[30px] pb-20 px-8 md:px-24 relative overflow-hidden">
          {/* Parallax background */}
          <div
            className="absolute inset-0 z-0"
            data-parallax
            style={{
              top: "-30%",
              bottom: "-30%",
              left: 0,
              right: 0,
              backgroundImage: latestGame
                ? `url(${mediaUrl(
                   teaserUrl ||
                   latestGame.Sections?.flatMap(s => s.Media || [])[0]?.url ||
                    ""
                    )})`
                : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "hue-rotate(20deg) opacity(1)"
            }}
          />
          <div className="absolute inset-0 z-10 tf-games-scrim" />
          <div className="absolute inset-0 z-10 tf-noise" />
          <div className="relative z-20">
            <h2 className="tf-section-title text-4xl font-bold mb-8 text-left">GAMES</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {latestGame && (
                /* CARD (NOT the whole Link to /games!) */
                
                <ParallaxCard
                initial={{ opacity: 0, x: 60 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: "easeOut" }}

                  className="
                    relative block rounded-md overflow-hidden
                    bg-[linear-gradient(to_top_right,_rgba(23,22,27,0.3),_rgba(135,35,65,0.3))]
                    backdrop-blur-lg p-6
                    shadow-md shadow-black/20
                    transition-transform duration-300 ease-in-out
                    hover:scale-[1.02]
                  "
                >
                  {/* inner link → specific game post */}
                  <Link to={`/games/${slugOrId(latestGame)}`} className="block">
                    {/* carousel */}
                    <div className="relative w-full h-64 mb-4 overflow-hidden rounded-md">
                      {slideImages.map((url, i) => (
                        <img
                          key={i}
                          src={mediaUrl(url)}
                          alt={`${latestGame.Title} slide ${i}`}
                          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                            i === currentSlide ? "opacity-100" : "opacity-0"
                          }`}
                        />
                      ))}
                    </div>
                    

                    <h3 className="text-2xl font-semibold mb-2">{latestGame.Title}</h3>
                    <p className="text-sm">{latestGame.TitleDescription}</p>
                  </Link>

                  {/* animated “View all games →” (separate navigation) */}
                  <div className="mt-4">
                    <AnimatedNavLink
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate("/games");
                      }}
                      className="inline-block underline text-white group-hover:text-twin-salmon"
                    >
                      View all games →
                    </AnimatedNavLink>
                  </div>
                  </ParallaxCard>
              )}
            </div>
          </div>
        </section>

        {/* ───────── Presskit ───────── */}
        <section id="presskit" className="scroll-mt-[-16px] relative overflow-hidden pt-[60px] pb-20 px-8 md:px-24 text-white">
          <div className="absolute inset-0" style={{ background: themeColors.presskitBg }} />
          <div className="absolute inset-0 tf-noise" />
          <div className="absolute inset-0 tf-vignette" />
          <div className="relative z-10">
          <h2 className="tf-section-title text-4xl font-bold mb-8">PRESSKIT</h2>
          <p className="max-w-3xl mb-6 text-white/90">
            Need assets, info or images? Our official presskit is coming soon.
          </p>
          {/* TODO: re-enable when presskit zip is ready
          <AnimatedButton text="Download Presskit" href="/downloads/twinframe-presskit.zip" />
          */}
          </div>
        </section>

        {/* ───────── Newsletter (mobile only) ───────── */}
        <MobileNewsletterStrip />

        {/* ───────── News ───────── */}
        <section id="news" className="scroll-mt-[-16px] relative overflow-hidden pt-[60px] pb-20 px-8 md:px-24 text-white">
          <div className="absolute inset-0" style={{ background: themeColors.newsBg }} />
          <div className="absolute inset-0 tf-noise" />
          <div className="absolute inset-0 tf-vignette" />
          <div className="relative z-10">
          <h2 className="tf-section-title text-4xl font-bold mb-8">LATEST NEWS</h2>

          {latestNews ? (
            /* wrapper div so the card is clickable to individual post */
            <ParallaxCard
              onClick={() => navigate(`/news/${slugOrId(latestNews)}`)}
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="
                group relative block max-w-4xl overflow-hidden
                rounded-md
                border border-white/0
                shadow-md shadow-black/20
                bg-black/20 backdrop-blur-lg
                transition-transform duration-300 hover:scale-105
                cursor-pointer
              "
            >
              <img
                src={mediaUrl(
                  latestNews.Sections?.flatMap(s => s.Media || [])[0]?.url || ""
                  )}
                alt={latestNews.Title}
                className="w-full h-72 object-cover rounded-md"
              />

              {/* TAG pills */}
               {Array.isArray(latestNews.Tags) && latestNews.Tags.length > 0 && (
                <div className="absolute top-3 right-3 flex flex-wrap gap-2 z-20">
                 {latestNews.Tags.map((t, i) => {
                 const label =
                  typeof t === "string"
                   ? t
                    : t?.Name || t?.name || t?.attributes?.Name;
                   if (!label) return null;
                  return (
                   <span
                    key={i}
                     className="
                     px-2 py-0.5 rounded-full
                      text-[10px] font-semibold uppercase tracking-wide
                     text-white
                    bg-[#872341]/90
                    backdrop-blur-sm
                     "
                 >
                 {label}
                 </span>
                 );
                 })}
               </div>
               )}
              
              {/* social metrics */}
                <div className="absolute bottom-3 right-3 flex gap-4 z-20 text-sm text-white/80">
                {/*
                  <span className="flex items-center gap-1">
                    <Heart className="w-4 h-4" /> 0
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-4 h-4" /> 0
                  </span>
                */}
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {latestNews?.views ?? latestNews?.attributes?.views ?? 0}
                  </span>
                </div>

              <div className="absolute bottom-0 left-0 w-full bg-black/80 px-6 py-4 rounded-md">
                {latestNews.PublishDate && (
                  <p className="text-sm text-white/70 mb-1">
                    {new Date(latestNews.PublishDate).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric"
                    })}
                  </p>
                )}

                <h3 className="text-2xl font-semibold text-white mb-2">{latestNews.Title}</h3>

                <AnimatedNavLink
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate("/news");
                  }}
                  className="underline text-white group-hover:text-twin-salmon"
                >
                  View all news →
                </AnimatedNavLink>
              </div>
              </ParallaxCard>
          ) : (
            <p className="text-lg">No news yet — stay tuned!</p>
          )}
          </div>
        </section>

        {/* ───────── Contact ───────── */}
        <section
  id="contact"
  className="scroll-mt-[-16px] relative overflow-hidden pt-[55px] pb-20 px-8 md:px-24 text-white"
>
  <div className="absolute inset-0" style={{ background: themeColors.contactBg }} />
  <div className="absolute inset-0 tf-noise" />
  <div className="absolute inset-0 tf-vignette" />
  <div className="relative z-10">
  <h2 className="tf-section-title text-4xl font-bold mb-8">CONTACT</h2>

  <div className="flex flex-col md:flex-row md:items-center justify-between w-full mt-12 gap-10">
  {/* Contact Form */}
  <form
    className="w-full md:w-1/2 space-y-4"
    onSubmit={async (e) => {
      e.preventDefault();
      try {
        await axios.post(
          `${API_BASE}/contact-messages`,
          {
            data: {
              Name: formData.name,
              Email: formData.email,
              Message: formData.message,
            },
          },
          { headers: { "Content-Type": "application/json" } }
        );
        setFormStatus("Message sent successfully!");
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => setFormStatus(null), 3000);
      } catch {
        setFormStatus("Failed to send message.");
        setTimeout(() => setFormStatus(null), 5000);
      }
    }}
  >
    {["name", "email"].map((field) => (
      <div key={field}>
        <label htmlFor={field} className="block text-sm font-semibold">
          {field === "name" ? "Your Name" : "Your Email"}
        </label>
        <input
          type={field === "email" ? "email" : "text"}
          id={field}
          name={field}
          required
          value={formData[field]}
          onChange={(e) =>
            setFormData((p) => ({ ...p, [field]: e.target.value }))
          }
          className="w-full p-3 rounded-lg bg-black/25 text-white border border-white/15 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#E17564]/50"
        />
      </div>
    ))}

    <div>
      <label htmlFor="message" className="block text-sm font-semibold">
        Your Message
      </label>
      <textarea
        id="message"
        name="message"
        rows="4"
        required
        value={formData.message}
        onChange={(e) =>
          setFormData((p) => ({ ...p, message: e.target.value }))
        }
        className="w-full p-3 rounded-lg bg-black/25 text-white border border-white/15 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#E17564]/50"
      />
    </div>

    <AnimatedButton text="Send Message" type="submit" />
    {formStatus && <p className="mt-4 text-sm italic">{formStatus}</p>}
  </form>

  {/* Discord Button */}
  <div className="w-full md:w-1/2 flex justify-center md:justify-center mt-8 md:mt-0">
    <a
      href="https://discord.gg/TdPsv5xujp"
      target="_blank"
      rel="noopener noreferrer"
      className="transition-transform duration-300 hover:scale-105 hover:brightness-105 drop-shadow-[0_12px_30px_rgba(0,0,0,0.35)]"
    >
      <img
        src="/images/discord.png"
        alt="Join us on Discord"
        className="w-[260px] h-auto max-w-full"
        style={{ objectFit: "contain" }}
      />
    </a>
  </div>
</div>


    {/* 👇 sentinel inside the contact section */}
    <div id="cta-sentinel-contact" className="h-px" />
  </div>

</section>

        <Footer />
          {/* floating wishlist + newsletter */}
        <FloatingCtaCard />
      </main>
    </>
  );
}
